import React, { useState } from 'react';
import { Box } from '@mui/material';
import AppleHealthChartTooltip from 'pages/Dashboard/components/AppleHealthChartTooltip';
import ChartAxisLabel from 'pages/Dashboard/components/ChartAxisLabel';
import { WaypointTracking } from 'pages/Dashboard/types/waypoints.types';
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Text,
  YAxis,
  Cell,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import Theme, { blue, cyan, green, grey } from 'theme';
import { getStartOfDayLocal } from 'utils/dateUtils';
import { lightBlue } from '@mui/material/colors';

// Custom label for ReferenceLine at right edge
function ReferenceLineLabel({
  viewBox,
  value,
}: {
  viewBox: { x: number; y: number; width: number };
  value: string;
}) {
  // x position should match the right edge of the chart (adjust as needed)
  // Use the actual chart width for dynamic positioning
  const { y, width } = viewBox;
  const x = width + 45;
  return (
    <text x={x} y={y} textAnchor='end' fontSize={10} fill={blue[700]}>
      {value}
    </text>
  );
}

const activityBarsMap: Record<
  string,
  {
    key: string;
    goalKey: string;
    color: string;
    normalColor: string;
    label: string;
    count: number;
    units: string;
    goalReachedCount: number;
  }
> = {
  activeEnergyBurned: {
    key: 'activeEnergyBurned',
    goalKey: 'activeEnergyBurnedGoal',
    color: cyan[700],
    normalColor: cyan[300],
    label: 'Move',
    count: 0,
    units: 'cal',
    goalReachedCount: 0,
  },
  appleExerciseTime: {
    key: 'appleExerciseTime',
    goalKey: 'appleExerciseTimeGoal',
    color: green[800],
    normalColor: green[300],
    label: 'Exercise',
    count: 0,
    units: 'min',
    goalReachedCount: 0,
  },
  appleStandHours: {
    key: 'appleStandHours',
    goalKey: 'appleStandHoursGoal',
    color: blue[800],
    normalColor: blue[300],
    label: 'Stand',
    count: 0,
    units: 'hr',
    goalReachedCount: 0,
  },
};

type CustomTooltipProps = {
  isInDays: boolean;
  label?: number;
  payload?: Payload<string | number | (string | number)[], string | number>[] | undefined;
  active?: boolean;
};

function CustomTooltip({ active, payload, label, isInDays }: CustomTooltipProps) {
  const data = Object.values(activityBarsMap).map((bar) => ({
    key: bar.key,
    label: bar.label,
    color: bar.color,
    units: bar.units,
    value: payload?.[0]?.payload?.[bar.key] ?? 0,
  }));

  return (
    <AppleHealthChartTooltip
      data={data}
      isInDays={isInDays}
      active={active}
      label={label}
      payload={payload}
    />
  );
}
interface AppleHealthActivityChartProps {
  chartData: WaypointTracking[];
  chartGoals: Record<string, string>[];
  yAxisProps: {
    startTime: number;
    endTime: number;
    ticks: number[];
    isInDays: boolean;
  };
  barSize: number;
}

function AppleHealthActivityChart({
  chartData,
  chartGoals,
  yAxisProps: { ticks, startTime, endTime, isInDays },
  barSize,
}: AppleHealthActivityChartProps) {
  const [focusBar, setFocusBar] = useState(-1);
  const goals = React.useMemo(() => chartGoals?.reduce((a, g) => {
    if (g?.key && g?.value) {
      const intValue = !Number.isNaN(parseInt(g.value, 10)) ? parseInt(g.value, 10) : 0;
      return { ...a, [g.key]: intValue };
    }
    return a;
  }, {} as Record<string, number>), [chartGoals]);

  const data = React.useMemo(() => {
    Object.keys(activityBarsMap).forEach((key) => {
      activityBarsMap[key].count = 0;
      activityBarsMap[key].goalReachedCount = 0;
    });
    return chartData?.map((w) => {
      const date = getStartOfDayLocal((w?.timestamp as number) * 1000, isInDays);
      const metadata = w?.metadata?.reduce((a, m) => {
        if (m?.key && m?.value) {
          const intValue = !Number.isNaN(parseInt(m.value, 10)) ? parseInt(m.value, 10) : 0;
          if (activityBarsMap[m.key]) {
            activityBarsMap[m.key].count += 1;
            if (
              !!goals[activityBarsMap[m.key]?.goalKey]
              && intValue > goals[activityBarsMap[m.key]?.goalKey]
            ) {
              activityBarsMap[m.key].goalReachedCount += 1;
            }
          }
          return { ...a, [m.key]: intValue };
        }
        return a;
      }, {} as Record<string, string | number>);

      return {
        date,
        ...metadata,
      };
    });
  }, [chartData, goals, isInDays]);

  const MemoizedReferenceLineLabel = React.useMemo(() => ReferenceLineLabel, []);

  return (
    <Box flex={1} display='flex' flexDirection='column'>
      <Box marginTop='auto'>
        {Object.values(activityBarsMap).map((bar, index) => {
          const goalLabel = ({ viewBox }: {
            viewBox: { x: number; y: number; width: number };
          }) => <MemoizedReferenceLineLabel viewBox={viewBox} value={`${goals[bar.goalKey]} ${bar.units}`} />;
          return (
            <ResponsiveContainer width='100%' height={135} key={bar.key}>
              <BarChart
                data={data}
                onMouseMove={(state) => {
                  setFocusBar(state?.isTooltipActive ? state?.activeTooltipIndex ?? -1 : -1);
                }}
                onMouseLeave={() => {
                  setFocusBar(-1);
                }}
                margin={{ left: 5, bottom: 0, top: 0, right: 5 }}
              >
                <Tooltip
                  content={<CustomTooltip isInDays={isInDays} />}
                  wrapperStyle={{ zIndex: 1, background: lightBlue[50] }}
                  position={{ y: -50 }}
                  cursor={false}
                />
                <XAxis
                  dataKey='date'
                  type='number'
                  domain={[startTime, endTime]}
                  tick={<ChartAxisLabel isInDays={isInDays} />}
                  tickFormatter={() => ''}
                  ticks={ticks}
                  height={index === 2 ? 25 : 1}
                  tickLine={false}
                  tickSize={2}
                  fontSize={12}
                  stroke={grey[50]}
                />
                <YAxis
                  orientation='right'
                  hide={data.length === 0}
                  domain={[
                    0,
                    (dataMax: number) => {
                      const max = Number.isNaN(dataMax) ? 0 : dataMax;
                      const goalMax = goals[bar.goalKey] ?? 0;
                      const actualMax = max > goalMax * 2 ? max : goalMax * 2;
                      return actualMax;
                    },
                  ]}
                  ticks={[0]}
                  axisLine
                  tickLine={false}
                  width={40}
                  direction='right'
                  tickFormatter={() => `0 ${bar.units}`}
                  fontSize={10}
                  stroke={grey[50]}
                  color={Theme.palette.text.secondary}
                  tick={{ fill: Theme.palette.text.secondary }}
                />
                <Legend
                  verticalAlign='top'
                  content={(
                    <div
                      style={{
                        justifyContent: 'space-between',
                        fontSize: 12,
                        display: 'flex',
                        width: '100%',
                        alignItems: 'flex-end',
                        marginTop: 12,
                      }}
                    >
                      <Text style={{ color: 'black' }}>{bar.label}</Text>
                    </div>
                    )}
                />
                {goals[bar.goalKey] && (
                  <ReferenceLine
                    y={goals[bar.goalKey]}
                    stroke={blue[700]}
                    strokeDasharray='5 5'
                    label={goalLabel}
                  />
                )}
                <Bar
                  dataKey={bar.key}
                  fill={bar.color}
                  barSize={barSize}
                  radius={[3, 3, 0, 0]}
                >
                  {data.map((entry: Record<string, string | number>, cIndex) => {
                    const value = entry[bar.key] as number;
                    const color = value > goals[bar.goalKey] ? bar.color : bar.normalColor;
                    return (
                      <Cell
                        key={entry?.date}
                        fill={focusBar !== cIndex ? color : `${Theme.custom.colors.primaryMain}aa`}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          );
        })}
      </Box>
    </Box>
  );
}

export default AppleHealthActivityChart;
